// Pages/Banner/BannerList.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrash, FaSearch, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { setState } from '../../redux/features/filtersSlice';
import { useGetBannersQuery } from "../../redux/services/bannerService/banner.Services";
import CommonBreadcrumb from '../../common/CommonBreadcrumb';
import { FaImages, FaPlus } from "react-icons/fa";

function BannerList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const filter = useSelector((state) => state.filters.state);
    const [statusFilter, setStatusFilter] = useState(filter.status || '');
    
    // ✅ Use ref to track initial load
    const isInitialMount = useRef(true);

    // ✅ Create stable filter object using useMemo with all dependencies
    const memoizedFilter = useMemo(() => {
        return {
            page: filter.page || 1,
            limit: filter.limit || 10,
            search: filter.search || '',
            status: filter.status || '',
        };
    }, [filter.page, filter.limit, filter.search, filter.status]);

    // ✅ Using memoized filter with optimized options
    const { 
        data, 
        isLoading, 
        isError, 
        refetch,
        isFetching,
        error
    } = useGetBannersQuery(memoizedFilter, {
        // ✅ Critical: Prevent automatic refetching
        refetchOnMountOrArgChange: false,
        refetchOnReconnect: false,
        refetchOnFocus: false,
        // ✅ Keep previous data while fetching
        keepPreviousData: true,
        // ✅ Only refetch when dependencies change
        skip: false,
        // ✅ Don't poll
        pollingInterval: 0,
    });

    // ✅ Extract data with proper fallbacks
    const bannerData = data?.data || [];
    const pagination = data?.pagination || {};
    const totalPages = pagination?.totalPages || 0;
    const totalItems = pagination?.total || 0;
    const currentPage = pagination?.page || 1;
    const showPagination = totalPages > 1;
    
    // ✅ Get image base URL from response or fallback to env
    const imageBaseUrl = useMemo(() => {
        if (data?.imageBaseUrl) {
            return data.imageBaseUrl;
        }
        return process.env.REACT_APP_API_URL || 'http://localhost:5000';
    }, [data?.imageBaseUrl]);

    // ✅ Memoize callbacks to prevent unnecessary re-renders
    const handlePageChange = useCallback((page) => {
        dispatch(setState({ page }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [dispatch]);

    const handleSearch = useCallback((value) => {
        dispatch(setState({
            search: value,
            page: 1,
        }));
    }, [dispatch]);

    const handleStatusFilter = useCallback((status) => {
        setStatusFilter(status);
        dispatch(setState({
            status: status,
            page: 1,
        }));
    }, [dispatch]);

    const clearFilters = useCallback(() => {
        setStatusFilter('');
        dispatch(setState({
            search: '',
            status: '',
            page: 1,
        }));
    }, [dispatch]);

    const handleLimitChange = useCallback((e) => {
        const newLimit = parseInt(e.target.value);
        dispatch(setState({
            limit: newLimit,
            page: 1,
        }));
    }, [dispatch]);

    const handleRefresh = useCallback(() => {
        refetch();
        successToast("Data refreshed successfully!");
    }, [refetch]);

    const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='40' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f1f3f5' rx='4'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='9' fill='%23adb5bd'%3ENo Image%3C/text%3E%3C/svg%3E";

    // ✅ Get image URL with proper fallback
    const getImageUrl = useCallback((imageName) => {
        if (!imageName) {
            return FALLBACK_IMAGE;
        }
        
        // ✅ Check if imageName already has protocol (full URL)
        if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
            return imageName;
        }
        
        const cleanName = imageName.replace(/^\//, '');
        
        // ✅ Use base URL from API response
        if (imageBaseUrl) {
            const base = imageBaseUrl.replace(/\/$/, '');
            if (cleanName.startsWith('uploads/')) {
                return `${base.replace(/\/uploads\/banner\/?$/, '')}/${cleanName}`;
            }
            return `${base}/${cleanName}`;
        }
        
        // ✅ Fallback to production URL
        return `https://api.bagdamahasabha.org/uploads/banner/${cleanName}`;
    }, [imageBaseUrl]);

    // ✅ Check if status is active
    const isActive = useCallback((status) => {
        return status === "active" || status === "Active" || status === 1 || status === true;
    }, []);

    // ✅ Handle status change
    const handleStatusChange = useCallback((item) => {
        dispatch(openModal({
            type: "status-banner",
            title: "Change Banner Status",
            data: {
                id: item.id,
                name: item.name,
                status: item.status,
                apiUrl: "/banner-status",
                message: `Are you sure you want to change status of "${item.name}"?`,
                onSuccess: handleRefresh,
            },
        }));
    }, [dispatch, handleRefresh]);

    // ✅ Handle view
    const handleView = useCallback((item) => {
        dispatch(openModal({
            type: "view-banner",
            title: "View Banner",
            data: { ...item, imageBaseUrl },
        }));
    }, [dispatch, imageBaseUrl]);

    // ✅ Handle edit
    const handleEdit = useCallback((item) => {
        dispatch(openModal({
            type: "edit-banner",
            title: "Edit Banner",
            data: { ...item, imageBaseUrl },
        }));
    }, [dispatch, imageBaseUrl]);

    // ✅ Handle delete
    const handleDelete = useCallback((item) => {
        dispatch(openModal({
            type: "delete-banner",
            title: "Delete Banner",
            data: {
                id: item.id,
                name: item.name,
                apiUrl: "/banner-delete",
                message: `Are you sure you want to delete "${item.name}"?`,
                onSuccess: handleRefresh,
            },
        }));
    }, [dispatch, handleRefresh]);

    // ✅ Debug: Log only on actual data changes
    useEffect(() => {
        if (data && !isInitialMount.current) {
            console.log('Banner Data updated:', {
                total: totalItems,
                count: bannerData.length,
                imageBaseUrl
            });
        }
    }, [data, totalItems, bannerData.length, imageBaseUrl]);

    // ✅ Set initial mount to false after first render
    useEffect(() => {
        isInitialMount.current = false;
    }, []);

    // ✅ Loading state
    if (isLoading && isInitialMount.current) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mt-3">Loading Banner data...</h5>
            </div>
        );
    }

    // ✅ Error state
    if (isError) {
        console.error('API Error:', error);
        return (
            <div className="text-center py-5">
                <div className="alert alert-danger">
                    <h5>Something Went Wrong</h5>
                    <p>Unable to load Banner data. Please try again.</p>
                    <p className="text-muted small">{error?.data?.message || error?.message || 'Unknown error'}</p>
                    <button className="btn btn-primary mt-2" onClick={handleRefresh}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* HEADER */}
            {/* <div className="page-header-breadcrumb all_breadcrumb">
                <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
                    <nav>
                        <h4 className="fw-semibold">
                            All Banners {totalItems > 0 && <span className="badge-custom bg-primary">{totalItems}</span>}
                            {isFetching && <span className="badge bg-warning ms-2">Updating...</span>}
                        </h4>
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item active">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item">All Banners</li>
                        </ol>
                    </nav>
                </div>
                <div className="page-title fw-semibold fs-18 mb-0">
                    <button
                        onClick={() => navigate("/add-banner")}
                        className="btn btn-primary me-2"
                    >
                        Add Banner
                    </button>
                </div>
            </div> */}
<CommonBreadcrumb
    title="All Banners"
    titleIcon={<FaImages className="me-2 text-primary" />}
    count={totalItems}
    extraBadge={
        isFetching && (
            <span className="badge bg-warning ms-2">
                Updating...
            </span>
        )
    }
    breadcrumbs={[
        { label: "Home", link: "/" },
        { label: "All Banners" },
    ]}
    buttons={[
        {
            text: "Add Banner",
            icon: <FaPlus className="me-1" />,
            onClick: () => navigate("/add-banner"),
            className: "btn btn-primary",
        },
    ]}
/>
            {/* SEARCH AND FILTER SECTION */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-4">
                            <SearchBar
                                search={filter.search}
                                setSearch={handleSearch}
                                placeholder="Search Banners by name..."
                            />
                        </div>

                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => handleStatusFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={filter.limit}
                                onChange={handleLimitChange}
                            >
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                        </div>

                        <div className="col-md-3 text-md-end">
                            {(filter.search || filter.status) && (
                                <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={clearFilters}
                                >
                                    <FaTimes className="me-1" />
                                    Clear Filters
                                </button>
                            )}
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleRefresh}
                                disabled={isFetching}
                            >
                                {isFetching ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    {(filter.search || filter.status) && (
                        <div className="mt-3 d-flex flex-wrap gap-2">
                            <span className="text-muted me-2">Active Filters:</span>
                            {filter.search && (
                                <span className="badge bg-primary d-flex align-items-center gap-1">
                                    Search: {filter.search}
                                    <button
                                        className="btn btn-link btn-sm text-white p-0 ms-1"
                                        onClick={() => handleSearch('')}
                                    >
                                        <FaTimes />
                                    </button>
                                </span>
                            )}
                            {filter.status && (
                                <span className="badge bg-info d-flex align-items-center gap-1">
                                    Status: {filter.status}
                                    <button
                                        className="btn btn-link btn-sm text-white p-0 ms-1"
                                        onClick={() => handleStatusFilter('')}
                                    >
                                        <FaTimes />
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* TABLE */}
            <div className="st-wrapper-card">
                {bannerData.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <FaSearch className="text-muted" size={48} />
                        </div>
                        <h5>No Banners Found</h5>
                        <p className="text-muted">
                            {filter.search || filter.status
                                ? "Try adjusting your search or filter criteria"
                                : "Click 'Add Banner' to create your first banner"}
                        </p>
                        {(filter.search || filter.status) && (
                            <button className="btn btn-primary" onClick={clearFilters}>
                                Clear Filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="st-table-responsive">
                        <table className="table st-custom-table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Image</th>
                                    <th>Banner Name</th>
                                    <th>Status</th>
                                    <th>Change Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bannerData.map((item, index) => {
                                    const imageUrl = getImageUrl(item.image);
                                    return (
                                        <tr key={item.id || index}>
                                            <td>
                                                {(currentPage - 1) * filter.limit + index + 1}
                                            </td>
                                            <td>
                                                <div className="position-relative">
                                                    <img 
                                                        src={imageUrl}
                                                        alt={item?.name || 'Banner'}
                                                        style={{
                                                            width: '60px',
                                                            height: '40px',
                                                            objectFit: 'cover',
                                                            borderRadius: '5px',
                                                            border: '1px solid #ddd',
                                                            backgroundColor: '#f8f9fa',
                                                        }}
                                                         onError={(e) => {
                                                             e.target.onerror = null;
                                                             e.target.src = FALLBACK_IMAGE;
                                                         }}
                                                        loading="lazy"
                                                    />
                                                </div>
                                            </td>
                                            <td className="fw-semibold">
                                                {item?.name || 'N/A'}
                                            </td>
                                            <td>
                                                <span className={`badge ${isActive(item.status) ? 'bg-success' : 'bg-danger'}`}>
                                                    {isActive(item.status) ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={{ width: "150px" }}>
                                                <div className="banner-status-switch">
                                                    <input
                                                        type="checkbox"
                                                        id={`status-${item.id}`}
                                                        className="banner-status-input"
                                                        checked={isActive(item.status)}
                                                        onChange={() => handleStatusChange(item)}
                                                    />
                                                    <label htmlFor={`status-${item.id}`} className="banner-status-label">
                                                        <span className="banner-status-ball"></span>
                                                        <span className="banner-status-text">
                                                            {isActive(item.status) ? "Active" : "Inactive"}
                                                        </span>
                                                    </label>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="st-action-btn st-view-btn me-1"
                                                    onClick={() => handleView(item)}
                                                    title="View Details"
                                                >
                                                    <FaEye />
                                                </button>

                                                <button
                                                    className="st-action-btn st-edit-btn me-1"
                                                    onClick={() => handleEdit(item)}
                                                    title="Edit Banner"
                                                >
                                                    <FaEdit />
                                                </button>

                                                <button
                                                    className="st-action-btn st-delete-btn"
                                                    onClick={() => handleDelete(item)}
                                                    title="Delete Banner"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}
                {showPagination && bannerData.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
                        <div className="text-muted small mb-2 mb-md-0">
                            Showing {((currentPage - 1) * filter.limit) + 1} to{' '}
                            {Math.min(currentPage * filter.limit, totalItems)} of {totalItems} entries
                        </div>
                        <Pagination
                            page={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default BannerList;